// src/components/BottomNav.js
import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Navbar from "react-bootstrap/Navbar";
import { Container } from "react-bootstrap";
import API from "../api";
import "../assets/css/bottom-nav.css";
import { useAuth } from "../AuthUser"; // ⬅️ for sign in/out

export default function BottomNav() {
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const params = new URLSearchParams(search);
    const auth = useAuth();
    const isLoggedIn = !!auth?.user;
    const isAdmin = auth?.user?.admin;

    // Active when on list or single video pages (covers /videos, /videos/..., /video/..., /video-... just in case)
    const onVideosPage =
        pathname === "/" ||
        /^\/videos(\/.*)?$/.test(pathname) ||
        /^\/video(\/.*)?$/.test(pathname);

    // JS-controlled dropups (no scroll listeners)
    const [videosOpen, setVideosOpen] = React.useState(false);
    const [moreOpen, setMoreOpen] = React.useState(false);

    // Categories
    const [cats, setCats] = React.useState([]);
    const [catsLoaded, setCatsLoaded] = React.useState(false);

    const currentSort = params.get("sort");        // 'new' | 'popular' | null
    const currentCategory = params.get("category"); // category id string

    // Fetch categories once
    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await API.get("/video/categories");
                const data = res?.data?.data || res?.data || [];
                const mapped = Array.isArray(data)
                    ? data.filter(Boolean).map((c) => ({
                        id:
                            c.id ??
                            c.category_id ??
                            c.value ??
                            c.slug ??
                            String(c.name || c.label || "category"),
                        name: (c.name || c.label || c.title || "Category").toString(),
                    }))
                    : [];
                if (alive) setCats(mapped);
            } catch {
                /* ignore */
            }
            if (alive) setCatsLoaded(true);
        })();
        return () => {
            alive = false;
        };
    }, []);

    // Close on outside click + ESC
    const rootRef = React.useRef(null);
    React.useEffect(() => {
        const onDown = (e) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) {
                setVideosOpen(false);
                setMoreOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                setVideosOpen(false);
                setMoreOpen(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    // --- Mobile stability helper ---
    // Measure the nav height once (and when the nav itself resizes or orientation changes)
    // and expose it as --bn-height so CSS can reserve space without reacting to iOS URL bar "resize".
    React.useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const setVar = () => {
            const h = Math.ceil(el.getBoundingClientRect().height);
            document.documentElement.style.setProperty("--bn-height", `${h}px`);
        };
        setVar();
        const ro = new ResizeObserver(setVar);
        ro.observe(el);

        const onOrient = () => setTimeout(setVar, 50);
        window.addEventListener("orientationchange", onOrient);

        return () => {
            ro.disconnect();
            window.removeEventListener("orientationchange", onOrient);
        };
    }, []);

    // Centered width for Videos dropup
    const computeVideosMenuStyle = React.useCallback(() => {
        const maxW = 320;
        const w = Math.min(maxW, Math.floor(window.innerWidth * 0.92));
        const left = Math.round((window.innerWidth - w) / 2);
        return { left, width: w };
    }, []);
    const [videosPos, setVideosPos] = React.useState(() => computeVideosMenuStyle());
    React.useEffect(() => {
        // keep this lightweight—only used to keep the dropup centered
        const onResize = () => setVideosPos(computeVideosMenuStyle());
        window.addEventListener("resize", onResize, { passive: true });
        return () => window.removeEventListener("resize", onResize);
    }, [computeVideosMenuStyle]);

    // Helpers
    const closeAllMenus = () => {
        setVideosOpen(false);
        setMoreOpen(false);
    };

    const navigateAndClose = (to) => {
        closeAllMenus();
        if (to === '#signout') {
            if (auth.signout) auth.signout(() => navigate('/videos', { replace: true }));
            else if (auth.signOut) auth.signOut();
            return;
        }
        if (to.startsWith('http')) {
            window.location.href = to;
        } else {
            navigate(to);
        }
    };


    // Safe navigate that always closes menu first
    const navigateAndCloseor = (to, external = false) => {
        closeAllMenus();
        // allow state to flush so the menu visually closes immediately
        setTimeout(() => {
            if (external) {
                window.location.assign(to);
            } else {
                navigate(to);
            }
        }, 0);
    };

    // Button handlers (restore old behavior)
    const onVideosClick = () => {
        if (!onVideosPage) {
            navigateAndClose("/videos");
            return; // first click goes to /videos
        }
        // already on videos -> toggle menu
        setVideosOpen((v) => !v);
        setMoreOpen(false);
    };
    const onMoreClick = () => {
        setMoreOpen((v) => !v);
        setVideosOpen(false);
    };

    // Logout handler (supports either auth.logout or auth.signOut)
    const handleLogout = async () => {
        try {
            if (typeof auth?.logout === "function") {
                await auth.logout();
            } else if (typeof auth?.signOut === "function") {
                await auth.signOut();
            }
        } finally {
            closeAllMenus();
            navigate("/");
        }
    };

    // Helpers for More active
    const isActivePath = (prefix) => pathname.startsWith(prefix);
    const isExactPath = (path) => pathname === path;

    return (
        <Navbar
            fixed="bottom"
            className="bg-dark-blue bn-navbar d-lg-none bottom-nav"
            role="navigation"
            aria-label="Primary mobile navigation"
            style={{ bottom: 0 }}
            ref={rootRef}
        >
            <Container fluid className="bottom-nav-bar position-relative justify-content-around">
                {/* VIDEOS (tiny caret; flips via CSS on [data-open='true']) */}
                <button
                    type="button"
                    className={`bn-btn bn-btn-videos ${onVideosPage ? "active" : ""}`}
                    onClick={onVideosClick}
                    aria-haspopup="menu"
                    aria-expanded={videosOpen ? "true" : "false"}
                    aria-label="Videos"
                    data-open={videosOpen ? "true" : "false"}
                >
                    <i className="bi bi-film" />
                    <span className="bn-label-with-caret">
                      Videos
                      <svg className="bn-caret" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
                        <path
                            d="M1 1l4 4 4-4"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                </button>

                {/* REVIEWS (also close menus on click) */}
                <NavLink
                    to="/reviews"
                    className={`bn-btn bn-btn-reviews ${isActivePath("/reviews") ? "active" : ""}`}
                    aria-label="Swing Reviews"
                    onClick={() => navigateAndClose("/reviews")}
                >
                    <i className="bi bi-collection-play" />
                    <span>Reviews</span>
                </NavLink>

                {/* FAB */}
                <NavLink
                    to="/upload-videos"
                    className="bn-fab"
                    aria-label="Upload Swing Review"
                    onClick={() => navigateAndClose("/upload-videos")}
                >
                    <i className="bi bi-plus-lg" />
                    <span className="visually-hidden">Upload</span>
                </NavLink>

                {/* DNA */}
                <NavLink
                    to="/dna-profile"
                    className={`bn-btn bn-btn-dna ${isActivePath("/dna-profile") ? "active" : ""}`}
                    aria-label="DNA Profile"
                    onClick={() => navigateAndClose("/dna-profile")}
                >
                    <i className="bi bi-person-circle" />
                    <span>DNA</span>
                </NavLink>

                {/* MORE */}
                <button
                    type="button"
                    className={`bn-btn bn-btn-more ${moreOpen ? "active" : ""}`}
                    onClick={onMoreClick}
                    aria-haspopup="menu"
                    aria-expanded={moreOpen ? "true" : "false"}
                    aria-label="More"
                    data-open={moreOpen ? "true" : "false"}
                >
                    <i className="bi bi-three-dots" />
                    <span>More</span>
                </button>

                {/* scrim */}
                {(videosOpen || moreOpen) && (
                    <button
                        className="bn-scrim show"
                        aria-hidden="true"
                        tabIndex={-1}
                        onClick={closeAllMenus}
                    />
                )}

                {/* VIDEOS DROPUP */}
                {videosOpen && (
                    <div
                        className="bn-dropup bn-dropup-videos"
                        role="menu"
                        style={{ left: videosPos.left, width: videosPos.width }}
                    >
                        <button
                            className={`bn-item bn-item-tight font-weight-bold  ${currentSort === "newest" ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("/videos?sort=newest")}
                        >
                            <i className="bi bi-stars" />
                            <span className="bn-item-label">New</span>
                        </button>
                        <button
                            className={`bn-item bn-item-tight font-weight-bold ${currentSort === "popular" ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("/videos?sort=popular")}
                        >
                            <i className="bi bi-graph-up" />
                            <span className="bn-item-label">Popular</span>
                        </button>

                        <div className="bn-sep" />

                        {!catsLoaded && (
                            <div className="px-3 py-2 text-muted small" role="status">
                                Loading…
                            </div>
                        )}
                        {cats.map((c) => {
                            const isActiveCat = String(currentCategory ?? "") === String(c.id ?? "");
                            return (
                                <button
                                    key={String(c.id)}
                                    className={`bn-item bn-item-cat ${isActiveCat ? "active" : ""}`}
                                    role="menuitem"
                                    onClick={() => navigateAndClose(`/videos?category=${encodeURIComponent(c.id)}`)}
                                >
                                    <span className="bn-item-label">{c.name}</span>
                                </button>
                            );
                        })}
                        {catsLoaded && cats.length === 0 && (
                            <button
                                className="bn-item bn-item-cat"
                                role="menuitem"
                                onClick={() => navigateAndClose("/videos")}
                            >
                                <span className="bn-item-label">Browse Categories…</span>
                            </button>
                        )}
                    </div>
                )}

                {/* MORE DROPUP */}
                {moreOpen && (
                    <div className="bn-dropup bn-dropup-more" role="menu">
                        <button
                            className="bn-item bn-item-tight bn-item-strong font-weight-bold text-secondary"
                            role="menuitem"
                            onClick={() => navigateAndClose("/pricing")}
                        >
                            <i className="fa fa-lock-open"/>
                            <span className="bn-item-label">Upgrade to Pro</span>
                        </button>

                        <div className="bn-sep" />
                        {auth?.user?.planType && (
                        <button
                            className={`bn-item ${isActivePath("https://meetings.hubspot.com/chris3535/mygolfdna") ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("https://meetings.hubspot.com/chris3535/mygolfdna")}
                        >
                            <i className="bi bi-bag-check" />
                            <span className="bn-item-label">PocketPRO</span>
                        </button>)}
                        <button
                            className={`bn-item ${isActivePath("/clinics/dual-force-golf-school") ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("/clinics/dual-force-golf-school")}
                        >
                            <i className="bi bi-flag" />
                            <span className="bn-item-label">Clinics</span>
                        </button>
                        <div className="bn-sep" />

                        <a
                            href="https://blog.mygolfdna.com"
                            className="bn-item bn-link-clean"
                            role="menuitem"
                            onClick={(e) => {
                                e.preventDefault();
                                navigateAndClose("https://blog.mygolfdna.com", true);
                            }}
                        >
                            <i className="bi bi-journal-text" />
                            <span className="bn-item-label">Blog</span>
                        </a>
                        <a
                            href="https://shop.mygolfdna.com"
                            className="bn-item bn-link-clean"
                            role="menuitem"
                            onClick={(e) => {
                                e.preventDefault();
                                navigateAndClose("https://shop.mygolfdna.com", true);
                            }}
                        >
                            <i className="bi bi-bag" />
                            <span className="bn-item-label">Training Aids</span>
                        </a>

                        <div className="bn-sep" />
                        <button
                            className={`bn-item ${isActivePath("/support") ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("/support")}
                        >
                            <i className="bi bi-life-preserver" />
                            <span className="bn-item-label">Support</span>
                        </button>
                        <button
                            className={`bn-item ${isActivePath("/account/") ? "active" : ""}`}
                            role="menuitem"
                            onClick={() => navigateAndClose("/account/")}
                        >
                            <i className="bi bi-gear" />
                            <span className="bn-item-label">Settings</span>
                        </button>
                        {isAdmin && (
                            <>
                            <button
                                className={`bn-item ${isActivePath("/admin/lessons") ? "active" : ""}`}
                                role="menuitem"
                                onClick={() => navigateAndClose("/admin/lessons")}
                            >
                                <i className="bi bi-speedometer2" />
                                <span className="bn-item-label">Admin Dashboard</span>
                            </button>
                            <div className="bn-sep" />
                            </>
                            )}
                            <button
                                className="bn-item"
                                role="menuitem"
                                onClick={() => navigateAndClose("#signout")}
                            >
                                <i className="bi bi-box-arrow-in-right" />
                                <span className="bn-item-label">Sign out</span>
                            </button>
                    </div>
                )}
            </Container>
        </Navbar>
    );
}
